/**
 * Task start and due dates are calendar dates, not instants: the API sends and
 * accepts `YYYY-MM-DD`, which is exactly what `<input type="date">` reads and
 * writes. So there is deliberately no conversion helper here for the input --
 * the value goes straight in and straight back out.
 *
 * The trap this module exists to avoid: `new Date('2026-09-06')` parses as UTC
 * midnight, so `toLocaleDateString()` renders 5 September anywhere west of
 * Greenwich. `formatDate` in ProjectCard is correct for `createdAt`/`updatedAt`,
 * which really are instants, and wrong for these.
 */

/** Parsed part-by-part, never through `new Date(string)`. */
export function formatDateOnly(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/** A local Date rendered as an input value. Reads the local getters, never
 *  `toISOString`, which gives tomorrow after 19:00 in UTC+5 and yesterday
 *  before 20:00 in UTC-4. */
function toInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Today in the reader's own timezone, as an input value. */
export function todayInputValue(): string {
  return toInputValue(new Date());
}

/**
 * A calendar date shifted by whole days.
 *
 * Goes through a real Date rather than adding to the day field of the string:
 * `setDate` rolls the month and the year over, and handles the 28th of February
 * and the 31st of December without either being a special case.
 */
export function shiftDateOnly(value: string, days: number): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  const shifted = new Date(year, month - 1, day);
  shifted.setDate(shifted.getDate() + days);
  return toInputValue(shifted);
}

/** Lexicographic comparison is chronologically correct for YYYY-MM-DD, which
 *  sidesteps the parsing problem entirely. */
export function isOverdue(dueDate: string | null): boolean {
  return dueDate !== null && dueDate < todayInputValue();
}

/** Local midnight on a calendar date, as an ISO instant -- what the time-entry
 *  API wants when someone logs "2 hours on Tuesday". */
export function dateInputToInstant(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year!, month! - 1, day!).toISOString();
}

/**
 * An ISO instant as an `<input type="datetime-local">` value, and back again.
 *
 * The header above says there is deliberately no conversion helper for the date
 * inputs, and that is exactly why these two exist: a calendar date's wire format
 * and `<input type="date">`'s value format are the same string, so there is
 * nothing to convert. A reminder is an instant on the wire and local wall-clock
 * in the input, so the two genuinely differ and something has to carry the
 * offset across.
 *
 * Local getters only. `toISOString().slice(0, 16)` is the same trap
 * `toInputValue` exists to avoid, one field wider -- it would show a reminder set
 * for 14:30 in Bahrain as 11:30, and then save 11:30 the next time the panel was
 * touched.
 */
export function instantToLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${toInputValue(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The reverse. Built part-by-part through the local `Date` constructor, never
 *  `new Date(string)`: the input omits the offset, and the two browsers that
 *  read a bare `YYYY-MM-DDTHH:mm` as UTC would shift every reminder by the
 *  reader's own offset. Seconds are dropped, which is what the input offers. */
export function localInputToInstant(value: string): string {
  const [datePart, timePart = '00:00'] = value.split('T');
  const [year, month, day] = (datePart ?? '').split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day, hours ?? 0, minutes ?? 0).toISOString();
}
